/** Cell-level hover popup — lists ALL assignments for that day (Figma 236-119919) */

import { ProcessedAssignment } from "../../type";
import { fmt, formatDuration } from "./helper";
import { getJobBarColorForTime } from "../../utils";
import { Plane, Settings, User, Wrench, AlertTriangle } from "lucide-react";
import {
  buildUnavailableContent,
  type MonthDayAvailability
} from "../availability-bands";

/**
 * Cell-level hover section describing AM/PM unavailability for a month day. The month cell
 * is the tooltip trigger (the wash overlay is pure-visual to avoid a nested tooltip), so this
 * renders inside that single tooltip — above the assignment list when both are present. AM and
 * PM are merged into one entry (a "Full day" block when both are out), matching the week/day
 * tooltips (WOR-2187).
 */
export function MonthlyCellAvailabilityContent({
  avail
}: {
  avail: MonthDayAvailability;
}) {
  if (!avail.am && !avail.pm) return null;
  const { title, reasons } = buildUnavailableContent(
    avail.am,
    avail.pm,
    avail.amReason,
    avail.pmReason
  );
  return (
    <div className="aero:w-[204px] aero:bg-white aero:rounded-lg aero:border aero:border-neutral-200 aero:shadow-xl aero:overflow-hidden aero:p-2">
      <div className="aero:flex aero:items-start aero:gap-1.5">
        <AlertTriangle className="aero:h-3 aero:w-3 aero:mt-[1px] aero:shrink-0 aero:text-red-600" />
        <div className="aero:flex aero:flex-col">
          <span className="aero:text-[11px] aero:font-semibold aero:text-red-600 aero:leading-snug">
            Unavailable — {title}
          </span>
          {reasons.map((reason, i) => (
            <span
              key={i}
              className="aero:text-[10px] aero:text-neutral-700 aero:leading-snug aero:break-words"
            >
              {reason}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MonthlyCellHoverContent({
  assignments
}: {
  assignments: ProcessedAssignment[];
}) {
  if (assignments.length === 0) return null;
  return (
    <div className="aero:w-[204px] aero:bg-white aero:rounded-lg aero:border aero:border-neutral-200 aero:shadow-xl aero:overflow-hidden aero:max-h-[480px] aero:overflow-y-auto">
      {assignments.map((a, i) => {
        const { text: barText } = getJobBarColorForTime(a.startDate, a.barVariant);

        return (
          <div
            key={String(a.id ?? i)}
            className="aero:p-2 aero:bg-white aero:border-b aero:border-neutral-100 last:border-b-0"
          >
            {/* Time + workstage + job order no */}
            <div className="aero:flex aero:flex-col aero:gap-0.5 aero:mb-2">
              <span className="aero:text-[9px] aero:text-neutral-900">
                {[
                  `${fmt(a.startDate)} – ${fmt(a.eddDate)}`,
                  formatDuration(a.duration)
                ]
                  .filter(Boolean)
                  .join("  ")}
              </span>
              <p
                className={`aero:text-[12px] aero:font-bold aero:leading-snug ${barText}`}
              >
                {a.processDesc ?? "—"}
              </p>
              <span className={`aero:text-[11px] aero:font-medium ${barText}`}>
                {a.jobOrderNo ?? "—"}
              </span>
            </div>
            {/* Divider */}
            <div className="aero:border-t aero:border-neutral-200 aero:mb-2" />
            {/* Icon rows */}
            <div className="aero:flex aero:flex-col aero:gap-1">
              <div className="aero:flex aero:items-center aero:gap-1.5">
                <Wrench className="aero:h-3 aero:w-3 aero:shrink-0 aero:text-neutral-500" />
                <span className="aero:text-[11px] aero:text-neutral-700 aero:truncate">
                  {a.partNo
                    ? `${a.partNo}${a.partDescription ? ` - ${a.partDescription}` : ""}`
                    : "—"}
                </span>
              </div>
              <div className="aero:flex aero:items-center aero:gap-1.5">
                <Plane className="aero:h-3 aero:w-3 aero:shrink-0 aero:text-neutral-500" />
                <span className="aero:text-[11px] aero:text-neutral-700 aero:truncate">
                  {a.customerName ?? "—"}
                </span>
              </div>
              <div className="aero:flex aero:items-center aero:gap-1.5">
                <User className="aero:h-3 aero:w-3 aero:shrink-0 aero:text-neutral-500" />
                <span className="aero:text-[11px] aero:text-neutral-700 aero:truncate">
                  {a.technicianName ?? "Unassigned"}
                </span>
              </div>
              <div className="aero:flex aero:items-center aero:gap-1.5">
                <Settings className="aero:h-3 aero:w-3 aero:shrink-0 aero:text-neutral-500" />
                <span className="aero:text-[11px] aero:text-neutral-700 aero:truncate">
                  {a.machineName ?? "—"}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
